import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { Button } from '@/components/shared/button/Button';
import { Card } from '@/components/shared/card/Card';
import { Loading } from '@/components/shared/Loading';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { ReadTemplate, useGetTemplateQuery, useUpdateTemplateMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { WorkingRoster } from '@/types/roster.types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FiCalendar, FiEdit2 } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { RenameTemplateModal } from './RenameTemplateModal';
import { TemplateEditor } from './TemplateEditor';
import { buildTemplateUpdate, useGqlTemplateToWorkingTemplate } from './templateHelper';

export const TemplateEditorPage: FC = () => {
  const { templateId } = useParams<{ templateId: string }>();

  const [templateResult, reexecuteTemplate] = useGetTemplateQuery({
    variables: { id: templateId ?? '' },
    pause: !templateId,
    requestPolicy: 'network-only'
  });

  const [{ fetching: saving }, updateTemplate] = useUpdateTemplateMutation();
  const gqlTemplateToWorkingTemplate = useGqlTemplateToWorkingTemplate();
  const [workingTemplate, setWorkingTemplateState] = useState<WorkingRoster | undefined>(undefined);
  const [renaming, setRenaming] = useState(false);

  const { registerForConfirmation, unregisterForConfirmation, saveNeeded, registerAction } = useConfirm(state => ({
    registerForConfirmation: state.registerForConfirmation,
    unregisterForConfirmation: state.unregisterForConfirmation,
    saveNeeded: state.confirmationNeeded,
    registerAction: state.registerConfirmAction
  }));

  const selectedTemplate = useMemo(
    () => (templateResult.data?.template as ReadTemplate) ?? undefined,
    [templateResult.data?.template]
  );

  useEffect(() => {
    if (selectedTemplate) {
      setWorkingTemplateState(gqlTemplateToWorkingTemplate(selectedTemplate));
      unregisterForConfirmation();
    }
  }, [gqlTemplateToWorkingTemplate, selectedTemplate, unregisterForConfirmation]);

  const setWorkingTemplate = useCallback(
    (template: WorkingRoster) => {
      registerForConfirmation(strings.daily.roster.unsaved, strings.daily.roster.leaveWithoutSaving);
      setWorkingTemplateState(template);
    },
    [registerForConfirmation]
  );

  const saveTemplate = useCallback(async () => {
    if (!selectedTemplate || !workingTemplate) {
      throw Error('Template must be selected before saving');
    }

    const result = await updateTemplate({
      updateTemplate: buildTemplateUpdate(workingTemplate, selectedTemplate)
    });

    await handleMutationResult(result, {
      successMessage: strings.daily.templates.saveSuccessMessage,
      errorMessage: strings.common.error,
      onSuccess: async data => {
        if (!data?.updateTemplate) return;

        await reexecuteTemplate({ requestPolicy: 'network-only' });
        unregisterForConfirmation();
      }
    });

    return result;
  }, [reexecuteTemplate, selectedTemplate, unregisterForConfirmation, updateTemplate, workingTemplate]);

  const buildRenamePayload = useCallback(
    (name: string) => {
      if (!selectedTemplate || !workingTemplate) {
        throw Error('Template must be selected before renaming');
      }

      return { ...buildTemplateUpdate(workingTemplate, selectedTemplate), name };
    },
    [selectedTemplate, workingTemplate]
  );

  const onTemplateRenamed = useCallback(async () => {
    await reexecuteTemplate({ requestPolicy: 'network-only' });
  }, [reexecuteTemplate]);

  useEffect(() => {
    if (saveNeeded) {
      registerAction({
        callback: saveTemplate,
        label: strings.daily.roster.saveAndLeave
      });
    }
  }, [registerAction, saveNeeded, saveTemplate]);

  const breadcrumbs: NavPage[] = useMemo(
    () => [
      {
        name: strings.daily.templates.listTitle,
        to: '/templates'
      },
      ...(selectedTemplate
        ? [
            {
              name: selectedTemplate.name,
              to: `/templates/${selectedTemplate.id}`
            }
          ]
        : [])
    ],
    [selectedTemplate]
  );

  if (!templateId) {
    return (
      <PageContainer fullWidth>
        <Card className="p-6">{strings.daily.templates.noTemplateSelected}</Card>
      </PageContainer>
    );
  }

  if (!templateResult.data && !templateResult.error) {
    return <Loading />;
  }

  return (
    <PageContainer
      lockHeader
      fullWidth
      customHeader={
        <PageHeader
          fullWidth
          pageTitle={selectedTemplate ? selectedTemplate.name : strings.daily.templates.editorFallbackTitle}
          icon={<FiCalendar />}
          breadcrumbs={breadcrumbs}
        >
          <div className="flex gap-3 items-center">
            {selectedTemplate && workingTemplate && (
              <Button compact onClick={() => setRenaming(true)}>
                <FiEdit2 className="h-4 w-4" />
              </Button>
            )}
            <Button primary disabled={!saveNeeded} loading={saving} onClick={saveTemplate}>
              {strings.common.save}
            </Button>
          </div>
        </PageHeader>
      }
    >
      <div className="h-full flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {selectedTemplate && workingTemplate ? (
            <TemplateEditor workingTemplate={workingTemplate} setWorkingTemplate={setWorkingTemplate} />
          ) : (
            <Card className="p-6">{strings.daily.templates.noTemplateSelected}</Card>
          )}
        </div>
      </div>
      <RenameTemplateModal
        open={renaming}
        onClose={() => setRenaming(false)}
        currentName={selectedTemplate?.name ?? ''}
        buildUpdatePayload={buildRenamePayload}
        onRenamed={onTemplateRenamed}
      />
    </PageContainer>
  );
};
